import React, { FunctionComponent, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

import { dayjs } from '@dw/dayjs';

import { STORAGE_KEYS } from '@dw/constants';
import {
  IAuthTokenResponse,
  IInitializeAuthResponse,
  IRefreshAuthTokenRequest,
  ISessionUser,
  IStoredAuthSession
} from '@dw/models/auth';
import { IReducerAction } from '@dw/models/shared';
import { useAppSettings } from '@dw/hooks';
import { recordAnalytic } from '@dw/services';
import { logger } from '@dw/services/logger';
import { cleanJsonResponse } from '@dw/utils';

const REFRESH_URL = '/v1/auth/refresh';

/**
 * Refresh this far ahead of expiry. An access token that is technically still valid but about to
 * lapse would otherwise send a request that expires in flight and comes back 401.
 */
const ACCESS_TOKEN_REFRESH_WINDOW_SECONDS = 120;

/**
 * Storage reads bypass the HTTP client's date revival, so everything read back out goes through
 * `cleanJsonResponse`. Anything unparseable is treated as absent rather than thrown.
 */
const parseStoredJson = <T,>(storedValue: string | null): T | null => {
  if (!storedValue) {
    return null;
  }

  try {
    return cleanJsonResponse(JSON.parse(storedValue)) as T;
  } catch {
    return null;
  }
}

const readStoredSession = (): IStoredAuthSession | null => {
  // localStorage first — that is where a "remember this device" session lives. Everything else went
  // to sessionStorage, so a tab that outlived its login simply finds nothing and starts clean.
  const session = parseStoredJson<IStoredAuthSession>(
    localStorage.getItem(STORAGE_KEYS.AuthSession) ?? sessionStorage.getItem(STORAGE_KEYS.AuthSession)
  );

  if (!session?.refreshToken || dayjs(session.refreshTokenExpiresAt).isBefore(dayjs())) {
    return null;
  }

  return session;
}

const readStoredSnapshot = (): IInitializeAuthResponse | null =>
  parseStoredJson<IInitializeAuthResponse>(
    localStorage.getItem(STORAGE_KEYS.AuthSnapshot) ?? sessionStorage.getItem(STORAGE_KEYS.AuthSnapshot)
  );

/**
 * The same signed-in identity. Compared on id AND email, not id alone: `Auth.context`'s
 * `/v1/auth/initialize` effect is keyed on this, so a changed email has to read as a new identity.
 */
const isSameUser = (a: ISessionUser | null | undefined, b: ISessionUser | null | undefined): boolean =>
  !!a && !!b && a.id === b.id && a.email === b.email;

/**
 * Whichever store owns the session owns the snapshot too. Declining "Remember this device" has to
 * mean nothing about the user survives the tab — the snapshot holds their name, school and role.
 */
const getSessionStores = (rememberDevice: boolean): [Storage, Storage] =>
  rememberDevice ? [localStorage, sessionStorage] : [sessionStorage, localStorage];

const writeStoredSession = (session: IStoredAuthSession): void => {
  const [store, otherStore] = getSessionStores(session.rememberDevice);

  store.setItem(STORAGE_KEYS.AuthSession, JSON.stringify(session));
  // Signing in again can flip which store owns the session — the loser must not linger and win the
  // localStorage-first read above.
  otherStore.removeItem(STORAGE_KEYS.AuthSession);
}

const writeStoredSnapshot = (snapshot: IInitializeAuthResponse, rememberDevice: boolean): void => {
  const [store, otherStore] = getSessionStores(rememberDevice);

  store.setItem(STORAGE_KEYS.AuthSnapshot, JSON.stringify(snapshot));
  otherStore.removeItem(STORAGE_KEYS.AuthSnapshot);
}

const clearStoredSnapshot = (): void => {
  localStorage.removeItem(STORAGE_KEYS.AuthSnapshot);
  sessionStorage.removeItem(STORAGE_KEYS.AuthSnapshot);
}

const clearStoredSession = (): void => {
  localStorage.removeItem(STORAGE_KEYS.AuthSession);
  sessionStorage.removeItem(STORAGE_KEYS.AuthSession);
  clearStoredSnapshot();
}

type TSessionContextActions = 'SET_SESSION' | 'SET_BOOTSTRAP_SNAPSHOT' | 'CLEAR_SESSION';

interface ISessionState {
  isInitialized: boolean;
  isAuthorized: boolean;
  authorizedUser: ISessionUser | null;
  /**
   * The last successful `/v1/auth/initialize` response, read from storage during the first render so
   * `Auth.context` can paint the dashboard before the background re-initialize returns.
   */
  bootstrapSnapshot: IInitializeAuthResponse | null;
}

interface ISessionContext extends ISessionState {
  /** The in-memory access token, refreshed first if it has expired or is about to. */
  getAccessToken: () => Promise<string | null>;
  /** Forces a single-flight refresh. Resolves to `null` when there is no session or the refresh failed. */
  refreshAccessToken: () => Promise<string | null>;
  getRefreshToken: () => string | null;
  /**
   * Whether a refresh token is still held. `getAccessToken` and `refreshAccessToken` both answer
   * `null` for two opposite situations - "the API ended this session" and "the API could not be
   * asked" - and only the first may end a session. This is how a caller tells them apart.
   */
  hasLiveSession: () => boolean;
  /** Adopts the tokens returned by `POST /v1/auth/otp/verify`. */
  startSession: (tokens: IAuthTokenResponse, rememberDevice: boolean) => void;
  persistBootstrapSnapshot: (snapshot: IInitializeAuthResponse) => void;
  /**
   * Local teardown only — it clears storage and state but revokes nothing. UI signs out through
   * `useSignOut()`, which revokes the token family server side first.
   */
  signOut: () => void;
}

const initialSessionState: ISessionState = {
  isInitialized: false,
  isAuthorized: false,
  authorizedUser: null,
  bootstrapSnapshot: null
}
const initialState: ISessionContext = {
  ...initialSessionState,
  getAccessToken: () => Promise.resolve(null),
  refreshAccessToken: () => Promise.resolve(null),
  getRefreshToken: () => null,
  hasLiveSession: () => false,
  startSession: () => {},
  persistBootstrapSnapshot: () => {},
  signOut: () => {}
}
export const SessionContext = React.createContext<ISessionContext>(initialState);

/**
 * A stored refresh token is treated as authorized without asking the API first — the whole point of
 * the optimistic startup. Only a failed refresh (§ `refreshAccessToken`) ends the session.
 */
const createInitialState = (restoredSession: IStoredAuthSession | null): ISessionState => ({
  ...initialSessionState,
  isInitialized: true,
  isAuthorized: !!restoredSession,
  authorizedUser: restoredSession?.user ?? null,
  bootstrapSnapshot: restoredSession ? readStoredSnapshot() : null
});

const SessionContextReducer = (
  state: ISessionState,
  action: IReducerAction<TSessionContextActions>
): ISessionState => {
  switch (action.type) {
    case 'SET_SESSION': {
      const authorizedUser = action.payload as ISessionUser;
      // Every rotation carries the same identity. Handing out a new object would restart
      // Auth.context's `/v1/auth/initialize` effect on each refresh.
      if (isSameUser(state.authorizedUser, authorizedUser)) {
        return state.isAuthorized ? state : { ...state, isAuthorized: true };
      }

      return { ...state, isInitialized: true, isAuthorized: true, authorizedUser };
    }
    case 'SET_BOOTSTRAP_SNAPSHOT':
      return { ...state, bootstrapSnapshot: action.payload };
    case 'CLEAR_SESSION':
      return { ...initialSessionState, isInitialized: true };
    default:
      return state;
  }
};

interface ISessionContextProviderProps {
  children: React.ReactNode;
}
export const SessionContextProvider: FunctionComponent<ISessionContextProviderProps> = (props: ISessionContextProviderProps) => {

  const { appSettings } = useAppSettings();

  // Read during the first render, not in an effect: AuthContextProvider and AuthGuard render below
  // this provider, and an effect would hand them a signed-out frame before the session appears.
  const sessionRef = useRef<IStoredAuthSession | null | undefined>(undefined);
  if (sessionRef.current === undefined) {
    sessionRef.current = readStoredSession();
  }

  const [state, dispatch] = useReducer(SessionContextReducer, sessionRef.current, createInitialState);

  const apiBaseUrlRef = useRef(appSettings.apiBaseUrl);
  const refreshRequestRef = useRef<Promise<string | null> | null>(null);
  const startedRef = useRef(false);

  /**
   * Bumped by every sign-out. A refresh that was already in flight resolves against the generation
   * it started in, so tokens that arrive after a teardown are dropped instead of resurrecting the
   * session the user just ended.
   */
  const sessionGenerationRef = useRef(0);

  useEffect(() => {
    // appsettings is refetched every 60 seconds; the refresh call reads the latest value off the ref
    // rather than re-creating (and re-running) everything below on each poll.
    apiBaseUrlRef.current = appSettings.apiBaseUrl;
  }, [appSettings.apiBaseUrl]);

  const signOut = useCallback((): void => {
    sessionGenerationRef.current += 1;
    sessionRef.current = null;
    clearStoredSession();
    dispatch({ type: 'CLEAR_SESSION' });
  }, []);

  const applyTokens = useCallback((tokens: IAuthTokenResponse, rememberDevice: boolean): void => {
    const session: IStoredAuthSession = {
      accessToken: tokens.accessToken,
      accessTokenExpiresAt: tokens.accessTokenExpiresAt,
      refreshToken: tokens.refreshToken,
      refreshTokenExpiresAt: tokens.refreshTokenExpiresAt,
      rememberDevice,
      user: {
        id: tokens.user.id,
        email: tokens.user.email
      }
    };

    // Storage BEFORE memory, deliberately. `refreshAccessToken` treats storage as the source of truth
    // for which token is live, so the ref must never be ahead of what is on disk — a rotation held only
    // in memory would later be refreshed from a stale stored token, which the API reads as a replay and
    // answers by revoking the whole family. If the write throws (quota, Safari private mode) this fails
    // loudly instead of diverging silently: the session keeps the token it already had, and the API's
    // lost-rotation recovery lets that token refresh again.
    writeStoredSession(session);
    sessionRef.current = session;
    dispatch({ type: 'SET_SESSION', payload: session.user });
  }, []);

  const refreshAccessToken = useCallback((): Promise<string | null> => {
    // Single flight: a burst of 401s, or several components asking for a token at once, share one
    // refresh — rotation means a second concurrent call would revoke the family it just issued.
    if (refreshRequestRef.current) {
      return refreshRequestRef.current;
    }

    if (!sessionRef.current) {
      return Promise.resolve(null);
    }

    // THE reconciliation point, and the reason `applyTokens` writes storage first. Rotation is single
    // use, so presenting a token another tab already spent reads as a replay server side and takes the
    // whole family, signing every tab out. `sessionRef` is loaded once during the first render and never
    // re-read, so any tab that was open while a sibling refreshed is holding a retired value — this
    // re-reads storage at the point of use so the newest token is always the one presented.
    //
    // Elsewhere `sessionRef` is a cache and that is fine: `getAccessToken` returning a stale access
    // token just falls through to here, and access tokens are not single use. Adding a storage read
    // there would put one on every HTTP request for no benefit.
    const session = readStoredSession() ?? sessionRef.current;
    sessionRef.current = session;

    const generation = sessionGenerationRef.current;

    const request = (async (): Promise<string | null> => {
      try {
        const requestBody: IRefreshAuthTokenRequest = { refreshToken: session.refreshToken };
        const response = await fetch(`${apiBaseUrlRef.current}${REFRESH_URL}`, {
          method: 'POST',
          cache: 'no-store',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });

        if (response.status === 401 || response.status === 403) {
          // The API has LOOKED at this token and refused it: revoked, replayed, idle past its 90
          // days, or rejected outright (a signing key rotated by a deploy reads the same way). Any
          // of those is terminal: keeping a session the API will never accept traps the user in a
          // redirect loop between the dashboard and login, because the optimistic snapshot keeps
          // re-hydrating a session that cannot work.
          signOut();
          return null;
        }

        if (!response.ok) {
          // Every OTHER failure says nothing about whether the token is still good, so the session
          // survives it and the next request tries again. That covers 5xx and an outage, but also
          // the 4xx that are not verdicts on the token: a 429 from a rate limiter, a 408, and above
          // all a 400 from a validation rule a newer API added to a shape an older client still
          // sends. This test used to be `>= 400 && < 500`, which turned every one of those into a
          // permanent sign-out and discarded a refresh token the API would happily have accepted.
          return null;
        }

        // This response never passes through the HTTP client, so the expiry fields need reviving.
        const tokens = cleanJsonResponse(await response.json()) as IAuthTokenResponse;

        if (generation !== sessionGenerationRef.current) {
          // Signed out while this was in flight — these tokens belong to a session that is over.
          return null;
        }

        applyTokens(tokens, session.rememberDevice);
        return tokens.accessToken;
      } catch (err) {
        // A thrown fetch is a network failure, not a verdict on the token.
        logger.error('Error during SessionContextProvider::refreshAccessToken', err);
        return null;
      } finally {
        refreshRequestRef.current = null;
      }
    })();

    refreshRequestRef.current = request;
    return request;
  }, [applyTokens, signOut]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const session = sessionRef.current;
    if (!session) {
      return null;
    }

    const secondsUntilExpiry = dayjs(session.accessTokenExpiresAt).diff(dayjs(), 'second');
    if (session.accessToken && secondsUntilExpiry > ACCESS_TOKEN_REFRESH_WINDOW_SECONDS) {
      return session.accessToken;
    }

    return await refreshAccessToken();
  }, [refreshAccessToken]);

  const getRefreshToken = useCallback((): string | null => sessionRef.current?.refreshToken ?? null, []);

  // Reads the ref, not React state: callers ask mid-request, and `signOut` clears the ref
  // synchronously while the `CLEAR_SESSION` re-render is still a tick away. Kept separate from
  // `getRefreshToken` rather than folded into `!!getRefreshToken()` so that asking "is this session
  // alive?" never has to hand the caller the credential itself.
  const hasLiveSession = useCallback((): boolean => !!sessionRef.current, []);

  const startSession = useCallback((tokens: IAuthTokenResponse, rememberDevice: boolean): void => {
    // A snapshot left by whoever signed in last must never paint over a fresh login.
    clearStoredSnapshot();
    dispatch({ type: 'SET_BOOTSTRAP_SNAPSHOT', payload: null });

    applyTokens(tokens, rememberDevice);
  }, [applyTokens]);

  const persistBootstrapSnapshot = useCallback((snapshot: IInitializeAuthResponse): void => {
    const session = sessionRef.current;
    if (!session) {
      return;
    }

    writeStoredSnapshot(snapshot, session.rememberDevice);
    dispatch({ type: 'SET_BOOTSTRAP_SNAPSHOT', payload: snapshot });
  }, []);

  useEffect(() => {
    // Startup runs exactly once. The 60 second appsettings poll re-renders this provider, and an
    // init keyed on `appSettings` used to re-run the whole auth bootstrap with it.
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    const session = sessionRef.current;
    if (!session) {
      // A snapshot with no session behind it is a leftover from a "don't remember me" login.
      clearStoredSession();
      return;
    }

    recordAnalytic('SessionRestored', {
      accessTokenSecondsRemaining: Math.max(0, dayjs(session.accessTokenExpiresAt).diff(dayjs(), 'second')),
      rememberedDevice: `${session.rememberDevice}`
    });

    // The app is already rendering; this only tops up the access token if it went stale.
    getAccessToken();
  }, [getAccessToken]);

  const context = useMemo<ISessionContext>(() => ({
    ...state,
    getAccessToken,
    refreshAccessToken,
    getRefreshToken,
    hasLiveSession,
    startSession,
    persistBootstrapSnapshot,
    signOut
  }), [state, getAccessToken, refreshAccessToken, getRefreshToken, hasLiveSession, startSession, persistBootstrapSnapshot, signOut]);

  return (
    <SessionContext.Provider value={context}>
      {props.children}
    </SessionContext.Provider>
  );
};
